import { useEffect, useState } from "react";
import { resolveMediaUrl } from "../services/educationService";

export type ResolvedImageStatus = "idle" | "loading" | "loaded" | "error";

export interface ResolvedImageResult {
	url: string | null;
	status: ResolvedImageStatus;
}

/**
 * Session cache of resolved media: input url → blob object URL.
 *
 * Only **bearer-fetched blob URLs** (auth-gated proxy paths) are cached, and
 * only after the image successfully decodes. Without this, navigating back into
 * a page re-bearer-fetched the bytes and minted a new blob every mount (and the
 * old behaviour revoked it on unmount), so the hero re-downloaded each visit.
 * Cached blobs are intentionally NOT revoked — the cache owns them for the
 * session (bounded by the number of distinct proxy media URLs viewed).
 *
 * Absolute presigned S3 URLs are deliberately NOT cached: they're short-lived
 * (~15-min TTL), so a session-long cache would serve an expired URL on revisit.
 * They pass through and rely on the browser's own HTTP cache instead.
 */
const resolvedMediaCache = new Map<string, string>();

/** Test-only: reset the session cache between cases. */
export function __clearResolvedMediaCache(): void {
	resolvedMediaCache.clear();
}

/**
 * Resolves an education-hub image URL for use in `<img>` / CSS `background-image`.
 *
 * In dev/LocalStack (`USE_ASSET_PROXY`) the API hands back a relative,
 * auth-gated path (`/api/education-hub/.../asset/image`). A CSS `url()` fetch
 * carries no Authorization header → 401 → no image. `resolveMediaUrl` fetches
 * the bytes with the bearer token and returns a blob object URL; absolute
 * presigned S3 URLs pass through unchanged. Successfully-decoded blob URLs are
 * cached for the session (see `resolvedMediaCache`) so repeat visits don't
 * re-fetch; presigned URLs are not cached (short TTL).
 *
 * Returns:
 *   - status "idle"    → url was empty/null — show realistic fallback
 *   - status "loading" → bearer-fetch in flight — show neutral skeleton
 *   - status "loaded"  → url is the resolved value — show hero
 *   - status "error"   → fetch failed — show realistic fallback
 */
export function useResolvedImageUrl(
	url: string | null | undefined,
): ResolvedImageResult {
	// Track which input the resolved value belongs to so a url change can't
	// briefly return the previous value. Seed from the cache so a cached image
	// renders on the very first paint (no loading flash on revisits).
	const [state, setState] = useState<{
		inputUrl: string | null;
		resolvedUrl: string | null;
		status: ResolvedImageStatus;
	}>(() => {
		const cached = url ? resolvedMediaCache.get(url) : undefined;
		return {
			inputUrl: url ?? null,
			resolvedUrl: cached ?? null,
			status: url ? (cached ? "loaded" : "loading") : "idle",
		};
	});

	useEffect(() => {
		if (!url) {
			setState({ inputUrl: null, resolvedUrl: null, status: "idle" });
			return;
		}

		// Cache hit → use it immediately, no fetch.
		const cached = resolvedMediaCache.get(url);
		if (cached) {
			setState({ inputUrl: url, resolvedUrl: cached, status: "loaded" });
			return;
		}

		// Immediately signal loading for this url.
		setState({ inputUrl: url, resolvedUrl: null, status: "loading" });

		let cancelled = false;
		resolveMediaUrl(url)
			.then((next) => {
				// Preload/decode BEFORE flipping to "loaded" so the image swaps in
				// cleanly with no placeholder/decoy flash (86ey1xqj0).
				const img = new Image();
				img.onload = () => {
					// Cache ONLY decoded blob URLs (proxy paths) — never presigned S3
					// (short TTL). Cache even if cancelled so the next visit reuses it.
					if (next.startsWith("blob:")) resolvedMediaCache.set(url, next);
					if (!cancelled)
						setState({ inputUrl: url, resolvedUrl: next, status: "loaded" });
				};
				img.onerror = () => {
					// Decode failed — evict any stale entry so a later mount retries
					// instead of returning this bad URL.
					resolvedMediaCache.delete(url);
					if (next.startsWith("blob:")) URL.revokeObjectURL(next);
					if (!cancelled)
						setState({ inputUrl: url, resolvedUrl: null, status: "error" });
				};
				img.src = next;
			})
			.catch(() => {
				resolvedMediaCache.delete(url);
				if (!cancelled)
					setState({ inputUrl: url, resolvedUrl: null, status: "error" });
			});
		return () => {
			cancelled = true;
		};
	}, [url]);

	// Only expose the resolved value once it matches the CURRENT url.
	if (state.inputUrl !== (url ?? null)) {
		const cached = url ? resolvedMediaCache.get(url) : undefined;
		if (cached) return { url: cached, status: "loaded" };
		return { url: null, status: url ? "loading" : "idle" };
	}
	return { url: state.resolvedUrl, status: state.status };
}
